"use client";
import { ChevronDownIcon } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";

interface DropdownItem {
  id: string;
  label: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
  description?: string;
  badge?: string;
  isNew?: boolean;
  disabled?: boolean;
}

interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  className?: string;
  isMobile?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  items,
  isOpen,
  onToggle,
  onClose,
  className,
  isMobile = false
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [lastHoverTime, setLastHoverTime] = useState<number>(0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only handle click outside for desktop mode
      if (!isMobile && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isMobile]);

  // Hide dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen && !isMobile) {
        onClose();
      }
    };

    if (isOpen && !isMobile) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, onClose, isMobile]);

  // Cleanup timeout on unmount and when dropdown state changes
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // Reset hover state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setIsHovering(false);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
    }
  }, [isOpen, hoverTimeout]);

  const handleToggle = (event: React.MouseEvent) => {
    // Prevent event bubbling to avoid conflicts with mobile menu
    event.stopPropagation();
    
    if (isMobile) {
      if (isOpen) {
        setIsAnimating(true);
        setTimeout(() => {
          onClose();
          setIsAnimating(false);
        }, 150);
      } else {
        // For mobile, just call onToggle once to open
        onToggle();
      }
    } else {
      // Desktop behavior - toggle on click
      onToggle();
    }
  };

  const handleMouseEnter = () => {
    // Only handle mouse events for desktop
    if (!isMobile) {
      const now = Date.now();
      setLastHoverTime(now);
      
      // Clear any existing timeout immediately
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      setIsHovering(true);
      
      // Debounce rapid hover events
      setTimeout(() => {
        if (Date.now() - lastHoverTime < 50) return; // Ignore rapid hovers
        onToggle();
      }, 50);
    }
  };

  const handleMouseLeave = () => {
    // Only handle mouse events for desktop
    if (!isMobile) {
      // Clear any existing timeout first
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      setIsHovering(false);
      
      // Set new timeout to close with longer delay
      const timeout = setTimeout(() => {
        // Double-check that we're still not hovering
        if (!isHovering) {
          onClose();
        }
      }, 500); // Increased delay for better UX
      setHoverTimeout(timeout);
    }
  };

  const handleDropdownMouseEnter = () => {
    // Only handle mouse events for desktop
    if (!isMobile) {
      // Clear any existing timeout when entering dropdown
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      setIsHovering(true);
    }
  };

  const handleDropdownMouseLeave = () => {
    // Only handle mouse events for desktop
    if (!isMobile) {
      // Clear any existing timeout first
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      setIsHovering(false);
      
      // Set new timeout to close with shorter delay for dropdown
      const timeout = setTimeout(() => {
        onClose();
      }, 300);
      setHoverTimeout(timeout);
    }
  };

  return (
    <div 
      ref={dropdownRef} 
      className={cn("relative", className)}
      onMouseEnter={!isMobile ? handleMouseEnter : undefined}
      onMouseLeave={!isMobile ? handleMouseLeave : undefined}
    >
      {/* Trigger Button */}
      <button
        onClick={handleToggle}
        className={cn(
          "flex items-center gap-1.5 px-3 py-2 rounded-md",
          "font-navigation font-[number:var(--navigation-font-weight)] text-white text-[length:var(--navigation-font-size)] leading-[var(--navigation-line-height)] tracking-[var(--navigation-letter-spacing)] [font-style:var(--navigation-font-style)]",
          "hover:text-blue-200 transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900",
          isOpen && "text-blue-200"
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {trigger}
        <ChevronDownIcon 
          className={cn(
            "w-5 h-5 transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          data-dropdown
          className={cn(
            "absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200",
            "transform transition-all duration-200 ease-out",
            isAnimating ? "opacity-0 scale-95 translate-y-[-8px]" : "opacity-100 scale-100 translate-y-0",
            "z-[9999]",
            isMobile && "relative top-0 left-0 mt-0 w-full bg-slate-800 border-slate-700 shadow-none rounded-none"
          )}
          style={{
            animation: isOpen && !isAnimating ? "dropdownSlideIn 0.2s ease-out" : undefined
          }}
          onMouseEnter={!isMobile ? handleDropdownMouseEnter : undefined}
          onMouseLeave={!isMobile ? handleDropdownMouseLeave : undefined}
        >
          {/* Arrow - only show on desktop */}
          {!isMobile && (
            <div className="absolute -top-1 left-6 w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45" />
          )}
          
          {/* Menu Items */}
          <div className={cn("py-2", isMobile && "py-0")}>
            {items.map((item, index) => {
              const isDisabled = item.disabled || !item.href;
              
              const content = (
                <>
                  {/* Icon */}
                  {item.icon && (
                    <item.icon className={cn(
                      "w-4 h-4",
                      isDisabled
                        ? isMobile ? "text-gray-400" : "text-gray-400"
                        : isMobile 
                          ? "text-gray-400 group-hover:text-blue-400 transition-colors duration-150" 
                          : "text-gray-400 group-hover:text-blue-500 transition-colors duration-150"
                    )} />
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {item.label}
                      </span>
                      {item.isNew && (
                        <span className={cn(
                          "px-1.5 py-0.5 text-xs rounded-full font-medium",
                          isMobile 
                            ? "bg-green-600 text-white" 
                            : "bg-green-100 text-green-700"
                        )}>
                          New
                        </span>
                      )}
                      {item.badge && (
                        <span className={cn(
                          "px-1.5 py-0.5 text-xs rounded-full font-medium",
                          isMobile 
                            ? "bg-blue-600 text-white" 
                            : "bg-blue-100 text-blue-700"
                        )}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className={cn(
                        "text-xs mt-0.5 line-clamp-2",
                        isMobile ? "text-gray-400" : "text-gray-500"
                      )}>
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Hover Effect - only on desktop and when not disabled */}
                  {!isMobile && !isDisabled && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                  )}
                </>
              );

              if (isDisabled) {
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 relative overflow-hidden",
                      "cursor-default pointer-events-none",
                      isMobile 
                        ? "text-gray-300" 
                        : "text-gray-700"
                    )}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    {content}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  href={item.href!}
                  onClick={(event) => {
                    // Dispatch navigation start event
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('navigation-start', {
                        detail: { href: item.href }
                      }));
                    }
                    
                    if (isMobile) {
                      event.stopPropagation();
                      onClose();
                    } else {
                      onClose();
                    }
                  }}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 transition-all duration-150 ease-in-out group relative overflow-hidden cursor-pointer",
                    isMobile 
                      ? "text-gray-300 hover:text-white hover:bg-slate-800" 
                      : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  )}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes dropdownSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-8px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};